import {
  BridgeErrorCode,
  IBridgeError,
  IBridgeMsg,
  RetrySafety,
} from '@/interfaces/print-bridge-protocol';

/**
 * Framework-free client for the printbridge/v1 WebSocket protocol.
 *
 * - request(method, params, timeoutMs) with an id -> resolver map
 * - on(event, cb) emitter for server events (hello, job.update, ...)
 * - reconnection with exponential backoff + jitter (500ms -> 10s) and a
 *   stable-connection backoff reset, modeled on the -pos socket-plugin
 *   transport
 *
 * The WebSocket constructor is injectable so node tests can pass the `ws`
 * package's client.
 */

export type BridgeClientStatus = 'disconnected' | 'connecting' | 'connected';

/** Error reported BY the bridge (res ok:false). */
export class PrintBridgeError extends Error {
  readonly code: BridgeErrorCode;
  readonly retrySafety: RetrySafety;

  constructor(error: IBridgeError) {
    super(error.message);
    this.name = 'PrintBridgeError';
    this.code = error.code;
    this.retrySafety = error.retrySafety;
  }
}

/** Local request failure (never reached the bridge / no answer). */
export class PrintBridgeRequestError extends Error {
  readonly reason: 'timeout' | 'disconnected';

  constructor(reason: 'timeout' | 'disconnected', method: string) {
    super(
      reason === 'timeout'
        ? `La solicitud "${method}" no obtuvo respuesta`
        : `Sin conexión con el plugin de impresión`
    );
    this.name = 'PrintBridgeRequestError';
    this.reason = reason;
  }
}

interface IReconnectConfig {
  initialDelayMs: number;
  maxDelayMs: number;
  multiplier: number;
  jitterPercent: number;
  stableThresholdMs: number;
}

const DEFAULT_RECONNECT_CONFIG: IReconnectConfig = {
  initialDelayMs: 500,
  maxDelayMs: 10000,
  multiplier: 2,
  jitterPercent: 20,
  stableThresholdMs: 30000,
};

const DEFAULT_REQUEST_TIMEOUT_MS = 10000;

function addJitter(delayMs: number, jitterPercent: number): number {
  const jitter = delayMs * (jitterPercent / 100);
  return delayMs + (Math.random() * jitter * 2 - jitter);
}

/**
 * The socket surface the client actually uses. The DOM WebSocket satisfies
 * it; node tests inject a thin adapter over the `ws` package (whose class is
 * not structurally a DOM WebSocket).
 */
export interface IBridgeSocket {
  onopen: ((event?: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event?: unknown) => void) | null;
  onerror: ((event?: unknown) => void) | null;
  send(data: string): void;
  close(): void;
}

type WebSocketCtor = new (url: string) => IBridgeSocket;

interface IPendingRequest {
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  method: string;
}

export interface IPrintBridgeClientOptions {
  url: string;
  reconnect?: Partial<IReconnectConfig>;
  /** injectable for node tests; defaults to the browser global */
  webSocketCtor?: WebSocketCtor;
}

export class PrintBridgeClient {
  private readonly url: string;
  private readonly config: IReconnectConfig;
  private readonly WebSocketImpl: WebSocketCtor;

  private socket: IBridgeSocket | null = null;
  private currentStatus: BridgeClientStatus = 'disconnected';
  private requestId = 0;
  private readonly pending = new Map<string, IPendingRequest>();
  private readonly eventListeners = new Map<
    string,
    Set<(payload: unknown) => void>
  >();
  private readonly statusListeners = new Set<
    (status: BridgeClientStatus) => void
  >();

  private currentBackoffMs: number;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  private isManualDisconnect = false;

  constructor(options: IPrintBridgeClientOptions) {
    this.url = options.url;
    this.config = { ...DEFAULT_RECONNECT_CONFIG, ...options.reconnect };
    this.WebSocketImpl = options.webSocketCtor ?? WebSocket;
    this.currentBackoffMs = this.config.initialDelayMs;

    if (typeof window !== 'undefined') {
      // Resume the retry loop when the browser comes back online (reconnects
      // are paused while offline).
      window.addEventListener('online', () => {
        if (this.currentStatus === 'disconnected' && !this.isManualDisconnect) {
          this.scheduleReconnect();
        }
      });
    }
  }

  get status(): BridgeClientStatus {
    return this.currentStatus;
  }

  onStatusChange(cb: (status: BridgeClientStatus) => void): () => void {
    this.statusListeners.add(cb);
    return () => {
      this.statusListeners.delete(cb);
    };
  }

  on(event: string, cb: (payload: unknown) => void): () => void {
    let set = this.eventListeners.get(event);
    if (!set) {
      set = new Set();
      this.eventListeners.set(event, set);
    }
    set.add(cb);
    return () => {
      set?.delete(cb);
    };
  }

  /** Idempotent: no-op while connecting/connected. */
  connect(): void {
    if (this.currentStatus !== 'disconnected') return;

    this.isManualDisconnect = false;
    this.setStatus('connecting');

    let socket: IBridgeSocket;
    try {
      socket = new this.WebSocketImpl(this.url);
    } catch (e) {
      console.error('[PrintBridge] failed to create socket', e);
      this.setStatus('disconnected');
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.setStatus('connected');
      this.clearRetryTimer();
      this.clearStableTimer();
      this.stableTimer = setTimeout(() => {
        this.currentBackoffMs = this.config.initialDelayMs;
      }, this.config.stableThresholdMs);
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      if (typeof event.data !== 'string') return;
      this.handleMessage(event.data);
    };

    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.clearStableTimer();
      this.rejectAllPending('disconnected');
      this.setStatus('disconnected');
      if (!this.isManualDisconnect) this.scheduleReconnect();
    };

    socket.onerror = () => {
      // onclose always follows; reconnection is handled there.
    };
  }

  disconnect(): void {
    this.isManualDisconnect = true;
    this.clearRetryTimer();
    this.clearStableTimer();

    const socket = this.socket;
    this.socket = null;
    if (socket) {
      // Detach before closing so onclose doesn't double-handle.
      socket.onclose = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onopen = null;
      try {
        socket.close();
      } catch (e) {
        console.warn('[PrintBridge] error closing socket', e);
      }
    }

    this.rejectAllPending('disconnected');
    this.setStatus('disconnected');
  }

  /** Skip the pending backoff delay and try to connect right now. */
  forceReconnect(): void {
    if (this.currentStatus !== 'disconnected') return;
    this.clearRetryTimer();
    this.currentBackoffMs = this.config.initialDelayMs;
    this.connect();
  }

  request<T = unknown>(
    method: string,
    params?: unknown,
    timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS
  ): Promise<T> {
    if (!this.socket || this.currentStatus !== 'connected') {
      return Promise.reject(
        new PrintBridgeRequestError('disconnected', method)
      );
    }

    const id = `r${++this.requestId}`;
    const msg: IBridgeMsg = { kind: 'req', id, method, params: params ?? {} };

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending.delete(id)) {
          reject(new PrintBridgeRequestError('timeout', method));
        }
      }, timeoutMs);

      this.pending.set(id, {
        resolve: (result) => resolve(result as T),
        reject,
        timer,
        method,
      });

      try {
        this.socket?.send(JSON.stringify(msg));
      } catch (e) {
        console.error('[PrintBridge] send failed', e);
        if (this.pending.delete(id)) {
          clearTimeout(timer);
          reject(new PrintBridgeRequestError('disconnected', method));
        }
      }
    });
  }

  private handleMessage(raw: string): void {
    let msg: IBridgeMsg;
    try {
      msg = JSON.parse(raw) as IBridgeMsg;
    } catch {
      console.warn('[PrintBridge] invalid message', raw.slice(0, 200));
      return;
    }
    if (typeof msg !== 'object' || msg === null) return;

    if (msg.kind === 'evt') {
      const listeners = this.eventListeners.get(msg.event);
      if (!listeners) return;
      for (const cb of [...listeners]) {
        try {
          cb(msg.payload);
        } catch (e) {
          console.error('[PrintBridge] event listener error', e);
        }
      }
      return;
    }

    if (msg.kind === 'res') {
      const entry = this.pending.get(msg.id);
      if (!entry) return;
      this.pending.delete(msg.id);
      clearTimeout(entry.timer);
      if (msg.ok === true) entry.resolve(msg.result);
      else entry.reject(new PrintBridgeError(msg.error));
    }
  }

  private rejectAllPending(reason: 'timeout' | 'disconnected'): void {
    for (const [, entry] of this.pending) {
      clearTimeout(entry.timer);
      entry.reject(new PrintBridgeRequestError(reason, entry.method));
    }
    this.pending.clear();
  }

  private setStatus(status: BridgeClientStatus): void {
    if (this.currentStatus === status) return;
    this.currentStatus = status;
    for (const cb of [...this.statusListeners]) {
      try {
        cb(status);
      } catch (e) {
        console.error('[PrintBridge] status listener error', e);
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.isManualDisconnect) return;
    // Explicit === false: environments without the field (node tests, some
    // webviews) must NOT be treated as offline.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Loopback target, but pausing while offline matches the legacy
      // transport and avoids pointless spinning on suspended laptops.
      return;
    }

    this.clearRetryTimer();
    const delay = addJitter(this.currentBackoffMs, this.config.jitterPercent);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
    this.currentBackoffMs = Math.min(
      this.currentBackoffMs * this.config.multiplier,
      this.config.maxDelayMs
    );
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private clearStableTimer(): void {
    if (this.stableTimer) {
      clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
  }
}
